import { PlusOutlined } from "@ant-design/icons";
import { Box, Button, Typography } from "@mui/material";
import type { UploadFile, UploadProps } from "antd";
import { Image, Upload } from "antd";
import { ErrorMessage, FieldProps, useField } from "formik";
import React, { JSX, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "styled-components";
import { deleteProductImage } from "../../service/products/ProductServices";
import ConfirmDialogBox from "../AlertBox/ConfirmDialogBox";
import { notifyError } from "../ToastContainer/Toast/Toast";
import pdfIcon from "../../Assets/FileIcon/pdf-icon.png";

type FileType = Parameters<UploadProps["beforeUpload"]>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const isPdfFile = (file: UploadFile): boolean => {
  const fileName = file.name?.toLowerCase() || "";
  const urlPath = (file.url || "").split("?")[0].toLowerCase();
  return (
    file.type === "application/pdf" ||
    fileName.endsWith(".pdf") ||
    urlPath.endsWith(".pdf")
  );
};

const getImageSrc = (file: UploadFile): string | undefined => {
  if (isPdfFile(file)) {
    return undefined;
  }
  const src = file.url || (file.preview as string) || "";
  return src || undefined;
};

const getPreviewableFiles = (files: UploadFile[]) =>
  files.filter((file) => Boolean(getImageSrc(file)));

const ensureImagePreviewSrcs = async (files: UploadFile[]): Promise<UploadFile[]> => {
  return Promise.all(
    files.map(async (item) => {
      if (isPdfFile(item) || item.url || item.preview) {
        return item;
      }
      if (!item.originFileObj) {
        return item;
      }
      try {
        item.preview = await getBase64(item.originFileObj as FileType);
      } catch (error) {
        console.error("Error reading image preview:", error);
      }
      return item;
    })
  );
};

interface ImageUploadProps extends FieldProps {
  maxFiles?: number;
  uploadTitleName?: string;
  caption?: string;
  outSideTitle?: string;
  isRequired?: boolean;
  disabled?: boolean;
  isEditMode?: boolean;
  multiple?: boolean;
  instantEditIcon?: (updatedList: any) => JSX.Element;
  instantDeleteIcon?: () => Promise<void>;
  imageBoxType?: "picture-card" | "picture-circle";
  className?: string;
  antValidation?: boolean;
  hideErrorMsg?: boolean;
  width?: string;
  isValidateType?: string[];
  maxFileSizeMB?: number;
}

const UploadWrapper = styled.div<{
  $error?: boolean;
  $imageBoxType?: "picture-card" | "picture-circle";
  $width?: string;
}>`
  .ant-upload {
    border-radius: ${({ $imageBoxType }) =>
      $imageBoxType === "picture-circle" ? "50%" : "8px"};
  }
  width: ${({ $width }) => $width || "100%"};

  ${({ $error, $imageBoxType }) => {
    if (!$error) return "";

    const borderColor = $imageBoxType === "picture-card" ? "red" : "#d9d9d9";
    const borderRadius = $imageBoxType === "picture-circle" ? "50%" : "8px";

    return `
      .ant-upload {
        border: 1px dashed ${borderColor} !important;
        border-radius: ${borderRadius};
      }
    `;
  }}
  .ant-upload {
    pointer-events: auto !important;
  }
    
  .ant-upload::before,
  .ant-upload::after {
    display: none !important;
    content: none !important;
  }

  .ant-upload.ant-upload-drag {
    pointer-events: none !important;
  }

  .ant-upload-select-picture-card,
  .ant-upload-select-picture-circle {
    border: none !important;
  }
`;


const StyledError = styled.div`
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const ImageUpload: React.FC<ImageUploadProps> = ({
  field,
  form,
  maxFiles = 1,
  uploadTitleName = "Upload",
  caption = "(Supported: JPEG,WEBP,PNG | Max: 2MB)",
  outSideTitle = "",
  isRequired = false,
  disabled = false,
  isEditMode = false,
  multiple = false,
  instantEditIcon = () => { },
  instantDeleteIcon = () => { },
  imageBoxType = "picture-card",
  className = "",
  antValidation = false,
  hideErrorMsg = false,
  width = "100%",
  isValidateType = [],
  maxFileSizeMB = 5
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedFileToDelete, setSelectedFileToDelete] = useState<UploadFile | null>(null);
  const hasShownErrorRef = useRef(false);
  const [, meta] = useField(field.name);
  const hasError = meta.touched && !!meta.error;
  // Format initial value if it contains URLs
  useEffect(() => {
    let formattedValue = typeof field.value === "string" ? [field.value] : field.value || [];
    if (Array.isArray(formattedValue) && formattedValue.length > 0) {
      const formattedFileList = formattedValue
        .filter(Boolean)
        .map((file, index) => {
          if (typeof file === "string") {
            // old format - no ID
            return {
              uid: `-url-${index}`,
              name: `${field.name.split('.')[1] || "file"}`,
              status: "done",
              url: file,
            };
          } else if (typeof file === "object" && file.url) {
            // new format - includes ID
            return {
              uid: file?.id || file?.uid || `-url-${index}`,
              name: `${field.name.split('.')[1] || "file"}`,
              status: "done",
              url: file.url,
            };
          }
          return file;
        })
        .filter(Boolean) as UploadFile[];

      setFileList(formattedFileList);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);


  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }
    const fileURL = file.url || (file.preview as string);
    const fileName = file.name?.toLowerCase() || '';
    const isPDF = file.type === 'application/pdf' || fileName.endsWith('.pdf');

    if (isPDF) {
      try {
        // Convert base64 to Blob for PDF preview
        const base64Content = fileURL.split(',')[1];
        const mimeType = fileURL.split(',')[0].match(/:(.*?);/)?.[1] || 'application/pdf';

        const byteCharacters = atob(base64Content);
        const byteArrays = new Uint8Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteArrays[i] = byteCharacters.charCodeAt(i);
        }

        const blob = new Blob([byteArrays], { type: mimeType });
        const pdfUrl = URL.createObjectURL(blob);
        window.open(pdfUrl, '_blank');
        // Cleanup after use. Reason:Memory is consumed in the user’s browser (client system).
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      } catch (error) {
        console.error('Error previewing PDF:', error);
        notifyError("Failed to preview PDF file. Please try again.");
        return;
      }

      return;
    } else if (file.url && file.url.endsWith('.pdf')) {
      const pdfUrl = file.url || (file.preview as string);
      window.open(pdfUrl, '_blank');
      // Cleanup after use. Reason:Memory is consumed in the user’s browser (client system).
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      return;
    }

    const updatedList = await ensureImagePreviewSrcs(fileList);
    setFileList(updatedList);

    const previewableFiles = getPreviewableFiles(updatedList);
    const nextIndex = previewableFiles.findIndex((item) => item.uid === file.uid);
    if (nextIndex < 0) {
      return;
    }

    setPreviewIndex(nextIndex);
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    const remainingSlots = maxFiles - fileList.length;

    let updatedList = [...fileList];

    if (newFileList.length > fileList.length && multiple) {
      const newlyAdded = newFileList.slice(fileList.length);
      const limitedNewlyAdded = newlyAdded.slice(0, remainingSlots);

      if (
        !hasShownErrorRef.current &&
        limitedNewlyAdded.length < newlyAdded.length
      ) {
        notifyError(`You can only upload ${remainingSlots} more image(s).`);
        hasShownErrorRef.current = true;

        setTimeout(() => {
          hasShownErrorRef.current = false;
        }, 100);
      }

      updatedList = [...fileList, ...limitedNewlyAdded];
    } else {
      updatedList = newFileList;
    }
    //Force thumbUrl for PDFs to make the eye icon appear
    updatedList = updatedList.map((file) => {
      const isPDF = isPdfFile(file);
      if (isPDF && !file.thumbUrl && !file.url && !file.preview) {
        return {
          ...file,
          thumbUrl: pdfIcon,
        };
      }
      return file;
    });
    instantEditIcon(updatedList);
    setFileList(updatedList);
    form.setFieldValue(field.name, updatedList);
    // form.setFieldTouched(field.name, true);
  };
  // const beforeUpload = (file: File) => {
  //   const isValidType = isValidateType.includes(file.type);
  //   const isValidSize = file.size <= 5 * 1024 * 1024;
  //   const isMinSize = file.size >= 5 * 1024;

  //   if (!isValidType) {
  //     notifyError('Only JPEG, PNG, JPG, or AVIF allowed')
  //     return Upload.LIST_IGNORE;
  //   }
  //   if (!isMinSize) {
  //     notifyError('File too small, Minimum 5KB allowed');
  //     return Upload.LIST_IGNORE;
  //   }
  //   if (!isValidSize) {
  //     notifyError('File too large, Max 5MB allowed');
  //     return Upload.LIST_IGNORE;

  //   }

  //   return false;
  // };

  const beforeUpload = (file: File) => {
    // If caller passed `isValidateType`, use it as the strict whitelist.
    // Otherwise, fall back to default allowed types.
    const allowedTypes =
      isValidateType && isValidateType.length > 0
        ? isValidateType
        : ["image/jpeg", "image/png", "image/jpg", "image/avif", "image/webp"];
  
    const isValidType = allowedTypes.includes(file.type);
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const isValidSize = file.size <= maxFileSizeBytes;
    const isMinSize = file.size >= 5 * 1024;          // 5KB
  
    if (!isValidType) {
      const isKycStrictSet =
        allowedTypes.length === 4 &&
        allowedTypes.includes("image/jpeg") &&
        allowedTypes.includes("image/png") &&
        allowedTypes.includes("image/jpg") &&
        allowedTypes.includes("application/pdf");

      if (isKycStrictSet) {
        notifyError("Only JPG, PNG, and PDF formats are allowed.");
      } else {
        const readableTypes = allowedTypes
          .map((type) => {
            if (type === "application/pdf") return "PDF";
            return type.split("/")[1]?.toUpperCase() || type;
          })
          .join(", ");
        notifyError(`Only ${readableTypes} files are allowed`);
      }
      return Upload.LIST_IGNORE;
    }
  
    if (!isMinSize) {
      notifyError("File too small, Minimum 5KB allowed");
      return Upload.LIST_IGNORE;
    }
  
    if (!isValidSize) {
      notifyError(`File size too large. Maximum allowed is ${maxFileSizeMB}MB.`);
      return Upload.LIST_IGNORE;
    }
  
    // Prevent auto-upload; we manage uploads via onChange.
    return false;
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>
        {uploadTitleName !== "Upload" ? (
          <>
            {uploadTitleName}
            {isRequired && <span style={{ color: "red", fontSize: "15px" }}>*</span>}
          </>
        ) : (
          <>{uploadTitleName}</>
        )}
      </div>
    </button>
  );
  const handleRemove = (file: UploadFile) => {
    setSelectedFileToDelete(file);
    setOpenConfirm(true);
    return false; // prevent default removal
  };

  const previewItems = useMemo(
    () =>
      getPreviewableFiles(fileList)
        .map((item) => getImageSrc(item))
        .filter((src): src is string => Boolean(src)),
    [fileList]
  );

  return (
    <Box>
      <UploadWrapper $error={hasError} $imageBoxType={imageBoxType} $width={width}>
        <Upload
          listType={imageBoxType}
          fileList={fileList}
          beforeUpload={(antValidation) ? beforeUpload : () => false}
          onPreview={handlePreview}
          onChange={handleChange}
          onRemove={handleRemove}
          style={{ width: width }}
          disabled={disabled}
          showUploadList={{ showRemoveIcon: !isEditMode }}
          multiple={multiple}
          className={className}
          accept=".png,.jpeg,.jpg,.pdf,.avif,.webp"
          onDrop={(e) => e.preventDefault()}
        >
          {fileList.length >= maxFiles ? null : uploadButton}
        </Upload>
      </UploadWrapper>
      <Typography variant="caption">
        {outSideTitle !== "" && (
          <>
            <strong>{outSideTitle}</strong>
            {isRequired && <span style={{ color: "red", fontSize: "15px" }}>*</span>}
            <br />
          </>
        )}
        {caption}
      </Typography>
      {!hideErrorMsg && <ErrorMessage name={field.name} component={StyledError} />}

      {previewItems.length > 0 && (
        <Image.PreviewGroup
          items={previewItems}
            preview={{
              visible: previewOpen,
              current: Math.min(previewIndex, previewItems.length - 1),
              onVisibleChange: (visible) => setPreviewOpen(visible),
              onChange: (current) => setPreviewIndex(current),
              getContainer: () => document.body,
              zIndex: 2000,
            }}
        />
      )}

      {openConfirm && <ConfirmDialogBox
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
          setSelectedFileToDelete(null);
        }}
        title="Confirm Image Deletion"
        content="Are you sure you want to delete this file or image?"
        actions={
          <>
            <Button
              onClick={() => {
                setOpenConfirm(false);
                setSelectedFileToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="secondary"
              onClick={async () => {
                if (selectedFileToDelete) {
                  //@ts-ignore
                  // eslint-disable-next-line no-mixed-operators
                  const fileId = (selectedFileToDelete.uid > 0) && selectedFileToDelete.uid || 0;

                  // try {
                  //   if (instantDeleteIcon) {
                  //     await instantDeleteIcon();                    
                  //   }
                  //     const updatedList = fileList.filter((item) => item.uid !== selectedFileToDelete.uid);
                  //     setFileList(updatedList);
                  //     form.setFieldValue(field.name, updatedList);
                  //     setOpenConfirm(false);
                  //     setSelectedFileToDelete(null);
                  //     return;
                  // } catch (error) {
                  //   notifyError(error?.response?.data?.message || error?.message || "Failed to delete file. Please try again.");
                  // }
                                    
                  try {
                    if (fileId) {  
                      await deleteProductImage(fileId);
                    } else {
                      if (instantDeleteIcon) {
                            await instantDeleteIcon();                    
                      }                      
                    }

                    const updatedList = fileList.filter((item) => item.uid !== selectedFileToDelete.uid);
                    setFileList(updatedList);
                    form.setFieldValue(field.name, updatedList);
                  } catch (error) {

                    notifyError("Failed to delete file. Please try again.");
                  }
                }
                const updatedList = fileList.filter((item) => item.uid !== selectedFileToDelete.uid);
                setFileList(updatedList);
                form.setFieldValue(field.name, updatedList);
                setOpenConfirm(false);
                setSelectedFileToDelete(null);
              }}
              autoFocus
            >
              Delete
            </Button>
          </>
        }
      />}
    </Box>
  );
};

export default ImageUpload;
